import { create } from 'zustand';
import { useAuthStore } from './authStore';
import { AIDiscovery, CreateDiscoveryRequest, ConfirmDiscoveryRequest, DiscoveryCandidate } from '../types/aiDiscovery';

interface AIDiscoveryState {
  discoveries: AIDiscovery[];
  isLoading: boolean;
  isUploading: boolean;
  isProcessing: boolean;
  error: string | null;
  getUploadUrl: (fileName: string, fileType: string, fileSize: number) => Promise<{ uploadUrl: string; fileKey: string }>;
  uploadFile: (file: File, uploadUrl: string) => Promise<void>;
  processDiscovery: (mediaKey: string, sourceType: 'audio' | 'image', timeZone?: string) => Promise<{ discoveryId: string; candidates: DiscoveryCandidate[] }>;
  fetchDiscoveries: (status?: string, includeHistory?: boolean, options?: { silent?: boolean }) => Promise<void>;
  getDiscoveryImageUrl: (discoveryId: string) => Promise<string>;
  confirmDiscovery: (discoveryId: string, candidateIndex: number, overrides?: Partial<ConfirmDiscoveryRequest>) => Promise<{ remainingCandidates: number }>;
  dismissDiscovery: (discoveryId: string) => Promise<void>;
  dismissCandidate: (discoveryId: string, candidateIndex: number) => Promise<{ remainingCandidates: number }>;
  clearError: () => void;
}

import { getApiUrl, getApiKey } from '../utils/apiConfig';

const API_URL = getApiUrl();
const API_KEY = getApiKey();

const getHeaders = (): HeadersInit => {
  const { idToken, isFamilyMode } = useAuthStore.getState();
  const headers: HeadersInit = {
    'Content-Type': 'application/json',
  };
  if (API_KEY) {
    headers['X-Api-Key'] = API_KEY;
  }
  if (idToken) {
    headers['Authorization'] = `Bearer ${idToken}`;
  }
  if (isFamilyMode) {
    headers['X-Family-Mode'] = 'true';
    headers['X-Attribution-Actor'] = 'family';
  }
  return headers;
};

export const useAIDiscoveryStore = create<AIDiscoveryState>((set, get) => ({
  discoveries: [],
  isLoading: false,
  isUploading: false,
  isProcessing: false,
  error: null,

  getUploadUrl: async (fileName: string, fileType: string, fileSize: number) => {
    set({ error: null, isUploading: true });
    try {
      const response = await fetch(`${API_URL}/ai/upload-url`, {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify({ fileName, fileType, fileSize }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ message: 'Failed to get upload URL' }));
        throw new Error(errorData.message || `HTTP ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error: any) {
      console.error('Error getting upload URL:', error);
      set({ error: error.message || 'Failed to get upload URL', isUploading: false });
      throw error;
    }
  },

  uploadFile: async (file: File, uploadUrl: string) => {
    set({ isUploading: true, error: null });
    try {
      const response = await fetch(uploadUrl, {
        method: 'PUT',
        body: file,
        headers: {
          'Content-Type': file.type,
        },
      });

      if (!response.ok) {
        throw new Error(`Upload failed: ${response.status}`);
      }
    } catch (error: any) {
      console.error('Error uploading file:', error);
      set({ error: error.message || 'Failed to upload file', isUploading: false });
      throw error;
    } finally {
      set({ isUploading: false });
    }
  },

  processDiscovery: async (mediaKey: string, sourceType: 'audio' | 'image', timeZone?: string) => {
    set({ isProcessing: true, error: null });
    try {
      const requestData: CreateDiscoveryRequest = {
        mediaKey,
        sourceType,
        timeZone: timeZone || Intl.DateTimeFormat().resolvedOptions().timeZone,
      };

      // Accept discovery (returns immediately with processing status)
      const response = await fetch(`${API_URL}/ai/discover`, {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify(requestData),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ message: 'Failed to accept discovery' }));
        throw new Error(errorData.message || `HTTP ${response.status}`);
      }

      const data = await response.json();
      set({ isProcessing: false });
      
      // Refresh discoveries to show the new processing item (silent - no loading flash)
      await get().fetchDiscoveries('processing', false, { silent: true });
      await get().fetchDiscoveries('pending', false, { silent: true });
      
      return data;
    } catch (error: any) {
      console.error('Error processing discovery:', error);
      set({ error: error.message || 'Failed to process discovery', isProcessing: false });
      throw error;
    }
  },

  fetchDiscoveries: async (status = 'pending', includeHistory = false, options?: { silent?: boolean }) => {
    if (!options?.silent) {
      set({ isLoading: true, error: null });
    }
    try {
      const params = new URLSearchParams();
      if (status) params.append('status', status);
      if (includeHistory) params.append('includeHistory', 'true');

      const url = `${API_URL}/ai/discoveries${params.toString() ? `?${params.toString()}` : ''}`;
      const response = await fetch(url, {
        method: 'GET',
        headers: getHeaders(),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ message: 'Failed to fetch discoveries' }));
        throw new Error(errorData.message || `HTTP ${response.status}`);
      }

      const data = await response.json();
      // Merge new discoveries with existing ones (update existing, add new)
      set((state) => {
        const existingMap = new Map(state.discoveries.map(d => [d.discoveryId, d]));
        const newDiscoveries = (data.discoveries || []) as AIDiscovery[];
        newDiscoveries.forEach(d => existingMap.set(d.discoveryId, d));
        const updates: Partial<AIDiscoveryState> = { discoveries: Array.from(existingMap.values()) };
        if (!options?.silent) updates.isLoading = false;
        return updates;
      });
    } catch (error: any) {
      console.error('Error fetching discoveries:', error);
      set(!options?.silent ? { error: error.message || 'Failed to fetch discoveries', isLoading: false } : { error: error.message || 'Failed to fetch discoveries' });
    }
  },

  getDiscoveryImageUrl: async (discoveryId: string) => {
    const response = await fetch(`${API_URL}/ai/discoveries/${discoveryId}/image-url`, {
      method: 'GET',
      headers: getHeaders(),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ message: 'Failed to get image URL' }));
      throw new Error(errorData.message || `HTTP ${response.status}`);
    }

    const data = await response.json();
    return data.imageUrl;
  },

  confirmDiscovery: async (discoveryId: string, candidateIndex: number, overrides?: Partial<ConfirmDiscoveryRequest>) => {
    set({ error: null });
    try {
      const requestData: ConfirmDiscoveryRequest = {
        candidateIndex,
        ...overrides,
      };

      const response = await fetch(`${API_URL}/ai/discoveries/${discoveryId}/confirm`, {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify(requestData),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ message: 'Failed to confirm discovery' }));
        throw new Error(errorData.message || `HTTP ${response.status}`);
      }

      const data = await response.json();
      const remainingCandidates = data.remainingCandidates ?? 0;

      // Update discovery: remove confirmed candidate, or remove discovery if no candidates left
      set((state) => {
        const discovery = state.discoveries.find((d) => d.discoveryId === discoveryId);
        if (!discovery) return state;
        if (remainingCandidates === 0) {
          return { discoveries: state.discoveries.filter((d) => d.discoveryId !== discoveryId) };
        }
        const updatedDiscovery = data.discovery
          ? { ...discovery, ...data.discovery }
          : { ...discovery, candidates: discovery.candidates.filter((_, i) => i !== candidateIndex), updatedAt: new Date().toISOString() };
        return {
          discoveries: state.discoveries.map((d) =>
            d.discoveryId === discoveryId ? updatedDiscovery : d
          ),
        };
      });

      return { remainingCandidates };
    } catch (error: any) {
      console.error('Error confirming discovery:', error);
      set({ error: error.message || 'Failed to confirm discovery' });
      throw error;
    }
  },

  dismissCandidate: async (discoveryId: string, candidateIndex: number) => {
    set({ error: null });
    const discovery = get().discoveries.find((d) => d.discoveryId === discoveryId);
    const hadMultipleCandidates = discovery && discovery.candidates.length > 1;

    // Optimistic: remove candidate from discovery
    set((state) => {
      const d = state.discoveries.find((x) => x.discoveryId === discoveryId);
      if (!d) return state;
      const remaining = d.candidates.filter((_, i) => i !== candidateIndex);
      if (remaining.length === 0) {
        return { discoveries: state.discoveries.filter((x) => x.discoveryId !== discoveryId) };
      }
      return {
        discoveries: state.discoveries.map((x) =>
          x.discoveryId === discoveryId ? { ...x, candidates: remaining, updatedAt: new Date().toISOString() } : x
        ),
      };
    });

    try {
      const response = await fetch(`${API_URL}/ai/discoveries/${discoveryId}/dismiss-candidate`, {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify({ candidateIndex }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ message: 'Failed to dismiss candidate' }));
        throw new Error(errorData.message || `HTTP ${response.status}`);
      }

      const data = await response.json();
      const remainingCandidates = data.remainingCandidates ?? 0;

      // Sync with server response (in case indices differ)
      if (remainingCandidates === 0 && hadMultipleCandidates) {
        set((state) => ({ discoveries: state.discoveries.filter((d) => d.discoveryId !== discoveryId) }));
      } else if (data.discovery) {
        set((state) => ({
          discoveries: state.discoveries.map((d) =>
            d.discoveryId === discoveryId ? { ...d, ...data.discovery } : d
          ),
        }));
      }

      return { remainingCandidates };
    } catch (error: any) {
      console.error('Error dismissing candidate:', error);
      if (discovery) {
        set((state) => ({
          discoveries: state.discoveries.map((d) => (d.discoveryId === discoveryId ? discovery : d)),
          error: error.message || 'Failed to dismiss candidate',
        }));
      } else {
        set({ error: error.message || 'Failed to dismiss candidate' });
      }
      throw error;
    }
  },

  dismissDiscovery: async (discoveryId: string) => {
    set({ error: null });

    // Optimistic update: remove from list immediately
    const dismissedDiscovery = get().discoveries.find((d) => d.discoveryId === discoveryId);
    set((state) => ({
      discoveries: state.discoveries.filter((d) => d.discoveryId !== discoveryId),
    }));

    try {
      const response = await fetch(`${API_URL}/ai/discoveries/${discoveryId}/dismiss`, {
        method: 'POST',
        headers: getHeaders(),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ message: 'Failed to dismiss discovery' }));
        throw new Error(errorData.message || `HTTP ${response.status}`);
      }
    } catch (error: any) {
      console.error('Error dismissing discovery:', error);
      // Rollback: restore the discovery on failure
      if (dismissedDiscovery) {
        set((state) => ({
          discoveries: [...state.discoveries, dismissedDiscovery].sort(
            (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
          ),
          error: error.message || 'Failed to dismiss discovery',
        }));
      } else {
        set({ error: error.message || 'Failed to dismiss discovery' });
      }
      throw error;
    }
  },

  clearError: () => set({ error: null }),
}));
